import { useEffect, useRef, useState } from "react";
import { Button, Form, Modal, Select } from "antd";
import Draggable from "react-draggable";

import Loading from "../../../common/Loading";
import { formItemLayout, inputLayout } from "../../../../constants/ApplicationConstants/FormConstants";
import CircularProgressBar from "../../../common/CircularProgressBar";
import useGetOneMasterList from "../../../../hooks/useGetOneMasterList";
import { ModuleAndIdMapping, Modules } from "../../../../constants/DatabaseConstants/MasterConstant";
import FormInputRules from "../../../../util/FormInputRules";
import { General } from "../../../../util/General";
import useGetWebUserList from "../../../../hooks/useGetWebUserList";
import { UserRoleDATA } from "../../../../constants/DatabaseConstants/AuthConstants";
import styles from "../../index.module.less";
import { useReactDraggable } from "../../../../hooks/useReactDraggable";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";

type UserRoleAllocationMasterInterface = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
};

const UserRoleAllocationMaster = (props: UserRoleAllocationMasterInterface) => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const formRef = useRef<FixMeLater>(null);

  /* STATES */
  const [formFieldsLoadedToForm, setFormFieldsLoadedToForm] = useState(false);
  const [roleName, setRoleName] = useState<string | null>(null);

  /* CUSTOM HOOKS */
  const { loading: roleListLoading, masterList: roleMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.ROLE_MASTER]);
  const { loading: userListLoading, userList } = useGetWebUserList();

  const { bounds, onStart } = useReactDraggable();

  /* HANDLERS */

  /* loading states from multiple hooks added as expression */
  const isOnAddLoading = !(props.addRecord && !roleListLoading && !userListLoading);
  const isOnEditLoading = !(!props.addRecord && !roleListLoading && !userListLoading);

  /* EFFECTS */
  useEffect(() => {
    if (!props.addRecord && !isOnEditLoading && !formFieldsLoadedToForm) {
      form.setFieldsValue(props.rowData);
      setRoleName(props.rowData?.role_name);
      setFormFieldsLoadedToForm(true);
    }

    // eslint-disable-next-line
  }, [isOnEditLoading]);

  return (
    <Modal
      afterClose={() => form.resetFields()}
      destroyOnClose={true}
      maskClosable={false}
      centered
      title={<div className="modal-container">{`${props.addRecord ? "Add" : "Edit"} User Role Allocation Master`}</div>}
      open={true}
      onCancel={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}
      width={"50%"}
      okButtonProps={{
        style: {
          display: "none"
        }
      }}
      cancelButtonProps={{ style: { display: "none" } }}
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
    >
      {((props.addRecord && isOnAddLoading) || (!props.addRecord && !formFieldsLoadedToForm)) && (
        <div className={`${styles["textCenter"]}`}>
          <Loading />
        </div>
      )}

      {((props.addRecord && !isOnAddLoading) || (!props.addRecord && formFieldsLoadedToForm)) && (
        <Form
          ref={formRef}
          onValuesChange={(changedValues) => {
            const fieldName = Object.keys(changedValues)[0];
            form.validateFields([fieldName]);
          }}
          className={`${styles["formDiv"]}`}
          {...formItemLayout}
          form={form}
          validateTrigger="onBlur"
          colon={false}
          name="register"
          onFinish={props.handleSubmit}
        >
          <Form.Item {...inputLayout} name="user_id" label="Username" rules={[FormInputRules.requiredFieldSelectRule("username")]}>
            <Select className={`${styles["from-Item-selectDiv"]}`} showSearch allowClear getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} placeholder="Select Username">
              {userList?.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.username}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item {...inputLayout} name="role_id" label="Role Name" rules={[FormInputRules.requiredFieldSelectRule("role name")]}>
            <Select className={`${styles["from-Item-selectDiv"]}`} showSearch allowClear dropdownStyle={{ maxHeight: "200px", overflowY: "auto" }} getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} placeholder="Select Role name">
              {/* WP Admin role skip to not select WP-Admin role */}
              {roleMasterList
                ?.filter((item) => {
                  if (!props.addRecord && roleName === UserRoleDATA.PARI_ADMIN) {
                    return true;
                  }
                  return item?.rolename !== UserRoleDATA.PARI_ADMIN;
                })
                ?.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.rolename}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button htmlType="submit" className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`}>
              {props.saving ? <CircularProgressBar /> : props.addRecord ? "Submit" : "Update"}
            </Button>
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button disabled={props.saving} className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`} onClick={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}>
              Close
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default UserRoleAllocationMaster;
